import React, { useEffect, useRef, useState } from "react";
import ApexCharts from "apexcharts";
import { Link } from "react-router-dom";
import CollapseHeader from "../../../core/common/collapse-header";
import { apiService } from "../../../services/api";
import { 
  DashboardFaturamentoResponse, 
  DashboardDisputasResponse,
  DashboardContratosAtivosResponse,
  DashboardFluxoCaixaResponse,
  DashboardPeriodo
} from "../../../types/dashboard";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";

const DashboardAnalytics = () => {
  // Separate Loading States
  const [loadingFaturamento, setLoadingFaturamento] = useState(true);
  const [loadingDisputas, setLoadingDisputas] = useState(true);
  const [loadingContratos, setLoadingContratos] = useState(true);
  const [loadingFluxoCaixa, setLoadingFluxoCaixa] = useState(true);
  
  // State for Data
  const [faturamentoData, setFaturamentoData] = useState<DashboardFaturamentoResponse | null>(null);
  const [disputasData, setDisputasData] = useState<DashboardDisputasResponse | null>(null);
  const [contratosData, setContratosData] = useState<DashboardContratosAtivosResponse | null>(null);
  const [fluxoCaixaData, setFluxoCaixaData] = useState<DashboardFluxoCaixaResponse | null>(null);

  // State for Filters
  const [faturamentoPeriodo, setFaturamentoPeriodo] = useState<DashboardPeriodo>('mensal');
  const [disputasPeriodo, setDisputasPeriodo] = useState<DashboardPeriodo>('semanal');
  const [fluxoCaixaPeriodo, setFluxoCaixaPeriodo] = useState<DashboardPeriodo>('mensal');

  // Refs for Charts
  const faturamentoChartRef = useRef(null);
  const disputasChartRef = useRef(null);

  // --- MOCK DATA GENERATORS (Fallback) ---
  const getMockFaturamento = (periodo: DashboardPeriodo): DashboardFaturamentoResponse => {
    const isYearly = periodo === 'anual';
    const count = isYearly ? 12 : (periodo === 'mensal' ? 30 : 7);
    const historico = Array.from({ length: count }, (_, i) => ({
      data: isYearly ? `Mês ${i+1}` : `Dia ${i+1}`,
      valor_real: Math.floor(Math.random() * 50000) + 20000,
      valor_objetivo: 50000
    }));

    const faturamentoTotal = historico.reduce((acc, curr) => acc + curr.valor_real, 0);

    return {
      success: true,
      data: {
        periodo: periodo,
        data_referencia: new Date().toISOString().split('T')[0],
        faturamento: {
          valor_real: faturamentoTotal,
          valor_objetivo: historico.reduce((acc, curr) => acc + curr.valor_objetivo, 0),
          percentual_alcance: 85,
          variacao_percentual: 5,
          status_indicador: 'success'
        },
        detalhes: {
          faturamento_total: faturamentoTotal,
          total_receitas: count,
          receita_media: Math.round(faturamentoTotal / count)
        },
        comparacao_periodo_anterior: {
          valor_anterior: Math.round(faturamentoTotal * 0.85),
          variacao: 12.5,
          periodo_anterior: '11/2025'
        },
        historico: historico
      }
    };
  };

  const getMockDisputas = (periodo: DashboardPeriodo): DashboardDisputasResponse => {
    return {
      success: true,
      data: {
        novos_oportunidades: { quantidade_real: 12, quantidade_objetivo: 15, status_indicador: 'warning' },
        oportunidades_perdidas: { quantidade_real: 2, quantidade_objetivo: 3, status_indicador: 'success' },
        oportunidades_suspensas: { quantidade_real: 3, quantidade_objetivo: 5, status_indicador: 'warning' },
        oportunidades_andamento: { quantidade_real: 25, quantidade_objetivo: 30, status_indicador: 'success' },
        disputas: { quantidade_real: 5, quantidade_objetivo: 10, status_indicador: 'warning' },
        historico: [] // Simplified for now
      }
    };
  };

  const getMockContratos = (): DashboardContratosAtivosResponse => ({
    success: true,
    data: {
      contrato_ativo_total: { quantidade_contratos: 142, valor_real: 0, valor_objetivo: 0, percentual_alcance: 0, status_indicador: 'neutral' },
      contratos_a_vencer: { quantidade_contratos: 15, valor_real: 0, valor_objetivo: 0, percentual_alcance: 0, status_indicador: 'danger' },
      novos_contratos_mes: { quantidade_contratos: 8, valor_real: 0, valor_objetivo: 0, percentual_alcance: 0, status_indicador: 'success' },
      renovacoes_contrato: { quantidade_renovacoes: 5, valor_real: 0, valor_objetivo: 0, percentual_alcance: 0, status_indicador: 'info' }
    }
  });

  const getMockFluxoCaixa = (): DashboardFluxoCaixaResponse => ({
    success: true,
    data: {
      data_base: new Date().toISOString().split('T')[0],
      periodo_analise: 'mensal',
      saldo_contas_correntes: { valor_real: 125000.50, valor_objetivo: 500000, percentual_alcance: 25, status_indicador: 'neutral', quantidade_contas: 3 },
      contas_a_pagar: { valor_real: 45000.00, quantidade_titulos: 18, vencidos: 2, maior_vencimento: '2025-12-30' },
      contas_a_receber: { valor_real: 68000.00, quantidade_titulos: 25, vencidos: 3, proximo_recebimento: '2025-12-26' },
      historico_por_periodo: Array.from({ length: 6 }, (_, i) => ({
        periodo: `2025-${String(12 - i).padStart(2, '0')}`,
        valor_entradas: Math.floor(Math.random() * 80000) + 20000,
        valor_saidas: Math.floor(Math.random() * 60000) + 10000,
        qtd_entradas: Math.floor(Math.random() * 20) + 10,
        qtd_saidas: Math.floor(Math.random() * 15) + 8,
        saldo: Math.floor(Math.random() * 40000) + 10000
      })),
      fluxo_previsto: {
        saldo_atual: 125000.50,
        entradas_30_dias: 160000,
        saidas_30_dias: 110000,
        saldo_projetado_30_dias: 175000.50,
        variacao_percentual: 40
      },
      // Compatibilidade com legado
      historico: Array.from({ length: 6 }, (_, i) => ({
        data: `Mês ${i+1}`,
        receitas: Math.floor(Math.random() * 80000) + 20000,
        despesas: Math.floor(Math.random() * 60000) + 10000
      }))
    }
  });

  // --- INDEPENDENT FETCH FUNCTIONS ---

  const fetchFaturamento = async () => {
    setLoadingFaturamento(true);
    try {
      let fatData;
      try {
        fatData = await apiService.getDashboardFaturamento({ periodo: faturamentoPeriodo, comparar_anterior: true });
        console.log("🟢 API Faturamento Response:", fatData);
      } catch (e) { 
        console.warn("🔴 API Faturamento Failed:", e); 
        fatData = getMockFaturamento(faturamentoPeriodo); 
      }
      setFaturamentoData(fatData);
    } catch (error) {
      console.error("Error fetching faturamento:", error);
    } finally {
      setLoadingFaturamento(false);
    }
  };

  const fetchDisputas = async () => {
    setLoadingDisputas(true);
    try {
      let dispData;
      try {
        dispData = await apiService.getDashboardDisputasContratos({ periodo: disputasPeriodo });
        console.log("🟢 API Disputas Response:", dispData);
      } catch (e) { 
        console.warn("🔴 API Disputas Failed:", e); 
        dispData = getMockDisputas(disputasPeriodo); 
      }
      setDisputasData(dispData);
    } catch (error) {
      console.error("Error fetching disputas:", error);
    } finally {
      setLoadingDisputas(false);
    }
  };

  const fetchContratos = async () => {
    setLoadingContratos(true);
    try {
      let contData;
      try {
        contData = await apiService.getDashboardContratosAtivos();
        console.log("🟢 API Contratos Response:", contData);
      } catch (e) { 
        console.warn("🔴 API Contratos Failed:", e); 
        contData = getMockContratos(); 
      }
      setContratosData(contData);
    } catch (error) {
      console.error("Error fetching contratos:", error);
    } finally {
      setLoadingContratos(false);
    }
  };

  const fetchFluxoCaixa = async () => {
    setLoadingFluxoCaixa(true);
    try {
      let fluxData;
      try {
        fluxData = await apiService.getDashboardFluxoCaixa({ periodo: fluxoCaixaPeriodo });
        console.log("🟢 API Fluxo Caixa Response:", fluxData);
      } catch (e) {
        console.warn("🔴 API Fluxo Failed:", e);
        fluxData = getMockFluxoCaixa();
      }
      setFluxoCaixaData(fluxData);
    } catch (error) {
      console.error("Error fetching fluxo caixa:", error);
    } finally {
      setLoadingFluxoCaixa(false);
    }
  };

  // --- EFFECTS ---

  useEffect(() => {
    fetchFaturamento();
  }, [faturamentoPeriodo]);

  useEffect(() => {
    fetchDisputas();
  }, [disputasPeriodo]);

  useEffect(() => {
    fetchContratos();
  }, []);

  useEffect(() => {
    fetchFluxoCaixa();
  }, [fluxoCaixaPeriodo]);

  // --- CHART RENDERING ---

  // 1. Faturamento
  useEffect(() => {
    if (faturamentoChartRef.current && faturamentoData?.data?.historico) {
        if (faturamentoChartRef.current) {
             (faturamentoChartRef.current as HTMLElement).innerHTML = '';
        }

        const hist = faturamentoData.data.historico;
        const options = {
            series: [
              { name: 'Real', data: hist.map(h => h.valor_real) },
              { name: 'Objetivo', data: hist.map(h => h.valor_objetivo) }
            ],
            chart: { type: 'area', height: 350, toolbar: { show: false } },
            dataLabels: { enabled: false },
            stroke: { curve: 'smooth' },
            xaxis: { categories: hist.map(h => h.data) },
            colors: ['#00918E', '#FFC38F'],
            tooltip: { y: { formatter: (val: number) => `R$ ${val.toLocaleString('pt-BR')}` } }
        };
        const chart = new ApexCharts(faturamentoChartRef.current, options);
        chart.render();
        return () => { chart.destroy(); };
    }
  }, [faturamentoData]);

  // 2. Disputas
  useEffect(() => {
    if (disputasChartRef.current && disputasData?.data) {
        if (disputasChartRef.current) {
             (disputasChartRef.current as HTMLElement).innerHTML = '';
        }

        const d = disputasData.data;
        const categories = ['Novos', 'Perdidos', 'Suspensos', 'Em Andamento', 'Disputas'];
        const realData = [
          d.novos_oportunidades.quantidade_real,
          d.oportunidades_perdidas.quantidade_real,
          d.oportunidades_suspensas.quantidade_real,
          d.oportunidades_andamento.quantidade_real,
          d.disputas.quantidade_real
        ];
        const goalData = [
          d.novos_oportunidades.quantidade_objetivo,
          d.oportunidades_perdidas.quantidade_objetivo,
          d.oportunidades_suspensas.quantidade_objetivo,
          d.oportunidades_andamento.quantidade_objetivo,
          d.disputas.quantidade_objetivo
        ];

        const options = {
            series: [
                { name: 'Real', data: realData },
                { name: 'Objetivo', data: goalData }
            ],
            chart: { type: 'bar', height: 350, toolbar: { show: false } },
            plotOptions: {
                bar: { horizontal: false, columnWidth: '55%', endingShape: 'rounded' },
            },
            dataLabels: { enabled: false },
            stroke: { show: true, width: 2, colors: ['transparent'] },
            xaxis: { categories: categories },
            colors: ['#28a745', '#ffc107'],
            fill: { opacity: 1 }
        };
        const chart = new ApexCharts(disputasChartRef.current, options);
        chart.render();
        return () => { chart.destroy(); };
    }
  }, [disputasData]);

  // Date Range Picker settings
  const initialSettings = {
    endDate: new Date(),
    ranges: {
      "Last 30 Days": [new Date(new Date().setDate(new Date().getDate() - 30)), new Date()],
      "This Month": [new Date(new Date().getFullYear(), new Date().getMonth(), 1), new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)],
    },
    startDate: new Date(new Date().setDate(new Date().getDate() - 7)), 
    timePicker: false,
  };

  const formatCurrency = (val: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);

  // Helper for Loading Overlay
  const LoadingOverlay = () => (
    <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10
    }}>
      <div className="spinner-border text-primary" role="status" />
    </div>
  );

  return (
    <div className="page-wrapper">
      <div className="content">
        
        {/* Page Header */}
        <div className="row">
          <div className="col-md-12">
            <div className="page-header">
              <div className="row align-items-center ">
                <div className="col-md-4">
                  <h3 className="page-title">Dashboard Analytics</h3>
                </div>
                <div className="col-md-8 float-end ms-auto">
                  <div className="d-flex title-head">
                    <div className="daterange-picker d-flex align-items-center justify-content-center">
                      <div className="form-sort me-2">
                        
                      </div>
                      <div className="head-icons mb-0">
                        <CollapseHeader/>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ROW 1: Faturamento (Real vs Objetivo) */}
        <div className="row">
          <div className="col-md-12">
            <div className="card flex-fill position-relative"> 
              <div className="card-header border-0 pb-0">
                 <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                    <h4><i className="ti ti-chart-area-line me-1" /> Faturamento (Real vs Objetivo)</h4>
                    <div className="d-flex align-items-center">
                        <div className="btn-group btn-group-sm" role="group">
                            {['diario', 'mensal', 'semestral', 'anual'].map((p) => (
                                <button 
                                    key={p}
                                    type="button" 
                                    className={`btn ${faturamentoPeriodo === p ? 'btn-primary' : 'btn-outline-primary'}`}
                                    onClick={() => setFaturamentoPeriodo(p as any)}
                                >
                                    {p.charAt(0).toUpperCase() + p.slice(1)}
                                </button>
                            ))}
                        </div>
                    </div>
                 </div>
              </div>
              <div className="card-body position-relative">
                 {loadingFaturamento && <LoadingOverlay />}
                 {faturamentoData ? (
                     <>
                        <div className="row mb-3 faturamento_info">
                            <div className="col-md-4 text-center">
                                <h5 className="text-muted">Real</h5>
                                <h3 className="text-success">{formatCurrency(faturamentoData.data.faturamento.valor_real)}</h3>
                            </div>
                            <div className="col-md-4 text-center">
                                <h5 className="text-muted">Objetivo</h5>
                                <h3>{formatCurrency(faturamentoData.data.faturamento.valor_objetivo)}</h3>
                            </div>
                            <div className="col-md-4 text-center">
                                <h5 className="text-muted">Alcance</h5>
                                <h3 className={(faturamentoData.data.faturamento.percentual_alcance ?? 0) >= 100 ? 'text-success' : 'text-warning'}>
                                    {faturamentoData.data.faturamento.percentual_alcance}%
                                </h3>
                            </div>
                        </div>
                        <div id="faturamento-chart" ref={faturamentoChartRef} />
                     </>
                 ) : (!loadingFaturamento && <div className="text-center p-5">Nenhum dado disponível.</div>)}
              </div>
            </div>
          </div>
        </div>

        {/* ROW 2: Operacional (Disputas, Contratos) */}
        <div className="row">
          <div className="col-md-8 d-flex">
             <div className="card flex-fill position-relative">
                <div className="card-header border-0 pb-0">
                    <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                        <h4><i className="ti ti-chart-bar me-1" /> Operacional: Real vs Objetivo</h4>
                        <div className="btn-group btn-group-sm">
                            <button className={`btn ${disputasPeriodo === 'diario' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setDisputasPeriodo('diario')}>Dia</button>
                            <button className={`btn ${disputasPeriodo === 'semanal' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setDisputasPeriodo('semanal')}>Semana</button>
                            <button className={`btn ${disputasPeriodo === 'mensal' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setDisputasPeriodo('mensal')}>Mês</button>
                            <button className={`btn ${disputasPeriodo === 'semestral' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setDisputasPeriodo('semestral')}>Semestre</button>
                            <button className={`btn ${disputasPeriodo === 'anual' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setDisputasPeriodo('anual')}>Ano</button>
                        </div>
                    </div>
                </div>
                <div className="card-body position-relative" style={{ minHeight: '350px' }}>
                    {loadingDisputas && <LoadingOverlay />}
                    <div id="disputas-chart" ref={disputasChartRef} />
                    {!loadingDisputas && !disputasData && <div className="text-center p-5">Nenhum dado disponível.</div>}
                </div>
             </div>
          </div>

          {/* KPI Cards for Operational Summary */}
          <div className="col-md-4 d-flex flex-column">
             {disputasData?.data ? (
                 <>
                    <div className="card flex-fill mb-2">
                        <div className="card-body p-3">
                            <div className="d-flex justify-content-between">
                                <span>Novas Oportunidades</span>
                                <span className="badge bg-success">{disputasData.data.novos_oportunidades.quantidade_real} / {disputasData.data.novos_oportunidades.quantidade_objetivo}</span>
                            </div>
                        </div>
                    </div>
                    <div className="card flex-fill mb-2">
                        <div className="card-body p-3">
                            <div className="d-flex justify-content-between">
                                <span>Oportunidades Perdidas</span>
                                <span className="badge bg-danger">{disputasData.data.oportunidades_perdidas.quantidade_real} / {disputasData.data.oportunidades_perdidas.quantidade_objetivo}</span>
                            </div>
                        </div>
                    </div>
                    <div className="card flex-fill mb-2">
                        <div className="card-body p-3">
                            <div className="d-flex justify-content-between">
                                <span>Disputas</span>
                                <span className="badge bg-warning">{disputasData.data.disputas.quantidade_real} / {disputasData.data.disputas.quantidade_objetivo}</span>
                            </div>
                        </div>
                    </div>
                    <div className="card flex-fill mb-2">
                        <div className="card-body p-3">
                            <div className="d-flex justify-content-between">
                                <span>Oportunidades Suspensas</span>
                                <span className="badge bg-info">{disputasData.data.oportunidades_suspensas.quantidade_real} / {disputasData.data.oportunidades_suspensas.quantidade_objetivo}</span>
                            </div>
                        </div>
                    </div>
                    <div className="card flex-fill mb-2">
                        <div className="card-body p-3">
                            <div className="d-flex justify-content-between">
                                <span>Em Andamento</span>
                                <span className="badge bg-secondary">{disputasData.data.oportunidades_andamento.quantidade_real} / {disputasData.data.oportunidades_andamento.quantidade_objetivo}</span>
                            </div>
                        </div>
                    </div>
                 </>
             ) : (loadingDisputas ? <div className="text-center"><div className="spinner-border text-primary" role="status" /></div> : null)}
          </div>
        </div>

        {/* ROW 3: Contratos (Financeiro) */}
        <div className="row dash_contratos">
           <div className="col-md-12">
               <h4 className="mb-3">Indicadores de Contratos</h4>
           </div>
           <div className="col-md-3">
              <div className="card bg-primary text-white">
                 <div className="card-body">
                    <h5>Contratos Ativos</h5>
                    <h2>{loadingContratos ? '...' : (contratosData?.data.contrato_ativo_total.quantidade_contratos || 0)}</h2>
                    <p className="mb-0 opacity-75">{loadingContratos ? '...' : (contratosData?.data.contrato_ativo_total.valor_real ? formatCurrency(contratosData.data.contrato_ativo_total.valor_real) : '-')}</p>
                 </div>
              </div>
           </div>
           <div className="col-md-3">
              <div className="card bg-danger text-white">
                 <div className="card-body">
                    <h5>Vencendo em 3 Meses</h5>
                    <h2>{loadingContratos ? '...' : (contratosData?.data.contratos_a_vencer.quantidade_contratos || 0)}</h2>
                    <p className="mb-0 opacity-75">{loadingContratos ? '...' : (contratosData?.data.contratos_a_vencer.valor_real ? formatCurrency(contratosData.data.contratos_a_vencer.valor_real) : '-')}</p>
                 </div>
              </div>
           </div>
           <div className="col-md-3">
              <div className="card bg-success text-white">
                 <div className="card-body">
                    <h5>Novos (Mês)</h5>
                    <h2>{loadingContratos ? '...' : (contratosData?.data.novos_contratos_mes.quantidade_contratos || 0)}</h2>
                    <p className="mb-0 opacity-75">{loadingContratos ? '...' : (contratosData?.data.novos_contratos_mes.valor_real ? formatCurrency(contratosData.data.novos_contratos_mes.valor_real) : '-')}</p>
                 </div>
              </div>
           </div>
           <div className="col-md-3">
              <div className="card bg-info text-white">
                 <div className="card-body">
                    <h5>Renovações</h5>
                    <h2>{loadingContratos ? '...' : (contratosData?.data.renovacoes_contrato.quantidade_renovacoes || 0)}</h2>
                    <p className="mb-0 opacity-75">{loadingContratos ? '...' : (contratosData?.data.renovacoes_contrato.valor_real ? formatCurrency(contratosData.data.renovacoes_contrato.valor_real) : '-')}</p>
                 </div>
              </div>
           </div>
        </div>

        {/* ROW 4: Fluxo de Caixa */}
        <div className="row mt-4">
           <div className="col-md-12">
                <div className="card flex-fill position-relative">
                    <div className="card-header border-0 pb-0">
                        <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                            <h4><i className="ti ti-cash me-1" /> Fluxo de Caixa</h4>
                            <div className="btn-group btn-group-sm">
                                <button className={`btn ${fluxoCaixaPeriodo === 'diario' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setFluxoCaixaPeriodo('diario')}>Dia</button>
                                <button className={`btn ${fluxoCaixaPeriodo === 'semanal' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setFluxoCaixaPeriodo('semanal')}>Semana</button>
                                <button className={`btn ${fluxoCaixaPeriodo === 'mensal' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setFluxoCaixaPeriodo('mensal')}>Mês</button>
                                <button className={`btn ${fluxoCaixaPeriodo === 'semestral' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setFluxoCaixaPeriodo('semestral')}>Semestre</button>
                                <button className={`btn ${fluxoCaixaPeriodo === 'anual' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setFluxoCaixaPeriodo('anual')}>Ano</button>
                            </div>
                        </div>
                    </div>
                    <div className="card-body position-relative">
                        {loadingFluxoCaixa && <LoadingOverlay />}
                        <div className="row">
                             <div className="col-md-4">
                                <div className="border p-3 rounded">
                                    <h6 className="text-muted">Saldo Contas Correntes</h6>
                                    <h4 className="text-primary">{fluxoCaixaData && formatCurrency(fluxoCaixaData.data.saldo_contas_correntes.valor_real)}</h4>
                                    <p className="mb-0 text-muted small">{fluxoCaixaData && fluxoCaixaData.data.saldo_contas_correntes.quantidade_contas} contas</p>
                                </div>
                             </div>
                             <div className="col-md-4">
                                <div className="border p-3 rounded">
                                    <h6 className="text-muted">Contas a Pagar</h6>
                                    <h4 className="text-danger">{fluxoCaixaData && formatCurrency(fluxoCaixaData.data.contas_a_pagar.valor_real)}</h4>
                                    <p className="mb-0 text-muted small">{fluxoCaixaData && fluxoCaixaData.data.contas_a_pagar.quantidade_titulos} títulos</p>
                                </div>
                             </div>
                             <div className="col-md-4">
                                <div className="border p-3 rounded">
                                    <h6 className="text-muted">Contas a Receber</h6>
                                    <h4 className="text-success">{fluxoCaixaData && formatCurrency(fluxoCaixaData.data.contas_a_receber.valor_real)}</h4>
                                    <p className="mb-0 text-muted small">{fluxoCaixaData && fluxoCaixaData.data.contas_a_receber.quantidade_titulos} títulos</p>
                                </div>
                             </div>
                        </div>
                    </div>
                </div>
           </div>
        </div>

      </div>
    </div>
  );
};

export default DashboardAnalytics;